/**
 * Femcare.life — Firebase Web Phone Auth glue.
 *
 * Uses the EXACT SAME Firebase project as the Fertile-Track mobile app (populated via
 * `REACT_APP_FIREBASE_*` env vars). All values here are client-safe public identifiers —
 * the sensitive Admin credential lives on Fertile-Track, not on Femcare.life.
 *
 * SMS OTP is dispatched by Firebase directly (Google's SMS pipeline), independent of
 * Fertile-Track's SMS provider. After the user enters the code, Firebase returns an
 * ID token which we forward to `POST /api/auth/firebase-phone-verify` on Fertile-Track
 * (already published) — FT then either resolves the existing canonical user or creates
 * a new one, and returns access/refresh tokens that our web adapter consumes server-side.
 *
 * ── reCAPTCHA singleton contract ─────────────────────────────────────────────
 * There must be EXACTLY ONE active RecaptchaVerifier per page load, bound to
 * exactly one DOM container. React StrictMode, re-renders, resend-clicks and
 * route re-entries must never construct a second verifier against the same node.
 * All flows must go through `getOrCreateRecaptchaVerifier()` and, when an
 * unrecoverable captcha error occurs, through `resetRecaptchaVerifier()`.
 * ──────────────────────────────────────────────────────────────────────────────
 */
import { initializeApp, getApps } from "firebase/app";
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

export const RECAPTCHA_CONTAINER_ID = "firebase-recaptcha-container";
const DEV = process.env.NODE_ENV !== "production";

const cfg = {
    apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId:             process.env.REACT_APP_FIREBASE_APP_ID,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
};

export function isFirebaseConfigured() {
    return Boolean(cfg.apiKey && cfg.authDomain && cfg.projectId && cfg.appId);
}

// ── module-level singletons (survive React re-renders + StrictMode double-invoke)
let _app  = null;
let _auth = null;
let _verifier = null;
let _verifierContainerId = null;

function ensureApp() {
    if (!isFirebaseConfigured()) {
        const e = new Error("firebase-not-configured");
        e.code = "femcare/firebase-not-configured";
        throw e;
    }
    if (!_app) {
        _app  = getApps()[0] || initializeApp(cfg);
        _auth = getAuth(_app);
    }
    return { app: _app, auth: _auth };
}

/**
 * Idempotent verifier accessor. Reuses an existing verifier if one is already
 * bound to the same container; otherwise creates a single new one. Never renders
 * twice against the same DOM node — React StrictMode / double-mount safe.
 */
export function getOrCreateRecaptchaVerifier(containerId = RECAPTCHA_CONTAINER_ID) {
    const { auth } = ensureApp();
    const el = document.getElementById(containerId);
    if (!el) {
        const e = new Error("recaptcha-container-not-mounted");
        e.code = "femcare/recaptcha-container-missing";
        throw e;
    }
    // If the container already has Firebase's iframe attached but our singleton was lost
    // (e.g. after a hot reload), clear the DOM subtree so a fresh verifier can attach.
    if (!_verifier && el.childElementCount > 0) {
        el.innerHTML = "";
    }
    if (_verifier && _verifierContainerId === containerId) {
        return _verifier;
    }
    // Different container from previous verifier → dispose the old one first.
    if (_verifier && _verifierContainerId !== containerId) {
        try { _verifier.clear && _verifier.clear(); } catch (_e) {}
        _verifier = null;
    }
    _verifier = new RecaptchaVerifier(auth, containerId, { size: "invisible" });
    _verifierContainerId = containerId;
    if (DEV) console.debug("[fc/firebase] created RecaptchaVerifier for", containerId);
    return _verifier;
}

/** Dispose the current verifier (call only when a captcha error invalidates it). */
export function resetRecaptchaVerifier() {
    try {
        if (_verifier && _verifier.clear) _verifier.clear();
    } catch (e) {
        if (DEV) console.debug("[fc/firebase] verifier.clear() threw", e);
    }
    const id = _verifierContainerId;
    _verifier = null;
    _verifierContainerId = null;
    // Also empty the DOM container so Firebase can render fresh next time.
    if (id) {
        const el = document.getElementById(id);
        if (el) el.innerHTML = "";
    }
}

/** Kick off Firebase phone-auth. Returns a ConfirmationResult which owns .confirm(code). */
export async function firebaseSendPhoneOtp(phoneE164, containerId = RECAPTCHA_CONTAINER_ID) {
    const { auth } = ensureApp();
    // Fresh verifier per Send-OTP call. Once a reCAPTCHA is solved (invisible or explicit)
    // it is consumed by Firebase — re-using it against the same DOM node produces
    // "reCAPTCHA has already been rendered in this element". Dispose any prior instance
    // FIRST, then create exactly one new verifier for this call.
    resetRecaptchaVerifier();
    const verifier = getOrCreateRecaptchaVerifier(containerId);
    try {
        return await signInWithPhoneNumber(auth, phoneE164, verifier);
    } catch (e) {
        // Verifier state after any error is not reusable — kill it so the next attempt is clean.
        resetRecaptchaVerifier();
        throw e;
    }
}

/**
 * Confirms the Firebase OTP against a prior ConfirmationResult and returns the
 * Firebase ID token that must be forwarded to /api/web/auth/firebase-phone-verify.
 */
export async function firebaseConfirmPhoneOtp(confirmationResult, code) {
    const cred = await confirmationResult.confirm(code);
    const idToken = await cred.user.getIdToken(/* forceRefresh */ true);
    return idToken;
}
